<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-card no-body>
            <div class="card-header">
                <b-col md="6" class="pl-0">
                    <b-card-title>
                        <slot name="header-label"/>
                    </b-card-title>
                </b-col>
                <b-col md="6">
                    <div class="float-right">
                        <slot name="header-controls"/>
                    </div>
                </b-col>
            </div>
            <div class="table-responsive">
                <table class="table b-table table-hover bordered overview-table">
                    <thead>
                        <tr>
                            <th class="pl-1 pr-0">{{ nameColumn.label }}</th>
                            <th v-for="field in activeFields">
                                <div>{{ field.label }}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody v-if="search" class="column-search">
                        <tr>
                            <td>
                                <slot name="filter-name">
                                    <b-form-input
                                        id="helperInput"
                                        :placeholder="nameColumn.label"
                                    />
                                </slot>
                            </td>
                            <td v-for="field in activeFields">
                                <slot :name="'filter-' + field.key" :field="field">
                                    <div></div>
                                </slot>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-if="items.length === 0">
                        <tr>
                            <td :colspan="activeFields.length + 1">
                                <div class="p-4 d-flex align-items-center justify-content-center" :style="minHeight">
                                    <b>Nothing found</b>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-for="product in this.items">
                        <tr @click="onRowClick(product)">
                            <td class="pl-1 pr-0 hierarchy-control">
                                <div style="display: flex;">
                                    <div @click.stop="toggleChildren(product, product)" style="margin-right: 10px">
                                        <feather-icon v-if="showFolderIcon(product)" :size="iconSize" :icon="isProductExpanded(product) ? 'FolderMinusIcon' : 'FolderPlusIcon'" />
                                        <feather-icon v-else :size="iconSize" icon="FileIcon" />
                                    </div>
                                    <div style="padding: 2px 8px 0 0;">
                                        {{ getName(product) }}
                                    </div>
                                </div>
                            </td>
                            <slot name="row" :item="product" :activeFields="activeFields">
                                <td v-for="field in activeFields">
                                    <slot :name="'cell-' + field.key" :item="product" :field="field">
                                        <div v-html="renderField(field, product)"></div>
                                    </slot>
                                </td>
                            </slot>
                        </tr>
                        <tr @click="onRowClick(child)" v-if="isProductParentExpanded(child) && isProductChildVisible(child, child.parent)" v-for="child in product.flatHierarchy">
                            <td class="hierarchy-control">
                                <div :style="calculatePadding(child) + ';display: flex;'">
<!--                                    <div :style="calculatePadding(child) + '; margin-right: 10px'" @click.stop="toggleChildren(child.__parent, child)">-->
                                    <div :style="'margin-right: 10px'" @click.stop="toggleChildren(child.__parent, child)">
                                        <feather-icon v-if="showFolderIcon(child)" :size="iconSize" :icon="isProductExpanded(child) ? 'FolderMinusIcon' : 'FolderPlusIcon'" />
                                        <feather-icon v-else :size="iconSize" icon="FileIcon" />
                                    </div>
                                    <div style="padding: 2px 0 0 0;">
                                        {{ getName(child) }}
                                    </div>
                                </div>
                            </td>
                            <slot name="row" :item="product" :activeFields="activeFields">
                                <td v-for="field in activeFields">
                                    <slot :name="'cell-' + field.key" :item="child" :field="field">
                                        <div v-html="renderField(field, child)"></div>
                                    </slot>
                                </td>
                            </slot>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="pl-2 pr-2 pb-2 d-flex justify-content-between flex-wrap">
                <b-form-group
                    :label="$t('Component(Grid):records-per-page')"
                    label-cols-md="6"
                    label-align="left"
                    label-for="sortBySelect"
                    class="text-nowrap mb-md-0 mr-1"
                >
                    <b-form-select
                        id="perPageSelect"
                        v-model="perPage"
                        @input="onPerPageChange"
                        inline
                        :options="pageOptions"
                    />
                </b-form-group>
                <div>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalItems"
                        :per-page="perPage"
                        @input="onPageChange"
                        first-number
                        last-number
                        prev-class="prev-item"
                        next-class="next-item"
                        class="mb-0"
                    >
                        <template #prev-text>
                            <feather-icon
                                icon="ChevronLeftIcon"
                                size="18"
                            />
                        </template>
                        <template #next-text>
                            <feather-icon
                                icon="ChevronRightIcon"
                                size="18"
                            />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </b-card>
    </b-overlay>
</template>
<script>
import {
    BCardText, BTable, BCardTitle, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup,
    BFormInput, BOverlay, BPagination, BFormSelect
} from 'bootstrap-vue'
import 'bootstrap-vue/dist/bootstrap-vue-icons.min.css'
import Ripple from 'vue-ripple-directive'
import {ProductType} from "@/libs/product";
import Table from "@/views/table/bs-table/Table";

export default {
    components: {
        Table,
        BCard, BCardText, BButton, BRow, BCol, BFormGroup, BCardGroup, BTable, BCardTitle,
        BFormInput, BOverlay, BPagination, BFormSelect
    },
    directives: {
        Ripple,
    },
    props: {
        search: {
            type: Boolean,
            default: false,
        },
        activeFields: {
            type: Array,
            default() {
                return [];
            }
        },
        fields: {
            type: Array,
            default() {
                return [];
            }
        },
        nameColumn: {
            type: Object,
            required: true,
        },
        nested: {
            type: Boolean,
            default: false
        },
        fetch: {
            default() {
                return () => {};
            }
        },
        pageOptions: {
            type: Array,
            default() {
                return [5, 15, 30];
            }
        },
        defaultPerPage: {
            type: Number,
            default: 15,
        }
    },
    data() {
        return {
            showExpandColumn: false,
            items: [],
            iconSize: '22',
            showLoading: true,
            currentPage: 1,
            perPage: this.defaultPerPage,
            totalItems: 0,
        }
    },
    methods: {
        onPerPageChange() {
            this.makeRequest();
        },
        onPageChange() {
            this.makeRequest();
        },
        showFolderIcon(product) {
            return product.children && product.children.length > 0;
        },
        renderValue(item, column) {
            if (item.hasOwnProperty(column.key)) {
                if (item[column.key] instanceof Object && item[column.key].hasOwnProperty('title')) {
                    return item[column.key].title;
                }

                return item[column.key];
            }

            return '';
        },
        getName(item) {
            if (item.name) {
                return item.name;
            }

            if (item.title) {
                return item.title;
            }

            return '';
        },
        initItems(items) {
            // return items;
            items.forEach((product) => {
                this.$set(product, '__expanded', false);
                product.flatHierarchy = this.flatten(product);
            });
            this.showExpandColumn = items.filter(p => p.children.length > 0).length > 0;
            return items;
        },

        gotoCreateProduct() {
            this.$router.push({name: 'product-new'});
        },

        renderField(field, product) {
            if (field.renderer) {
                return field.renderer(product);
            }

            return this.renderValue(product, field);
        },

        isProductParentExpanded(product) {
            return product.__parent.__expanded;
        },

        isProductExpanded(product) {
            return product.__expanded;
        },

        isProductChildVisible(product, parent) {
            return product.__visible;
        },

        collapseChildren(product) {
            if (product.flatHierarchy) {
                product.flatHierarchy.forEach(p => {
                    p.__expanded = false;
                    p.__visible = false;
                });
            } else {
                product.children.forEach(p => {
                    p.__visible = false;
                    p.__expanded = false;

                    this.collapseChildren(p);
                });
            }
        },

        toggleChildren(product, child) {
            if (product === child) {
                product.__expanded = !product.__expanded;
            } else {
                child.__expanded = !child.__expanded;
            }

            child.children.forEach(p => {
                p.__visible = !p.__visible
            });

            if (!child.__expanded) {
                this.collapseChildren(child);
            }
        },

        onRowClick(product) {
            this.$emit('click', product);
        },

        getDynamicFieldValue(product, type, name) {
            let p = product[type].find((r) => {
                return r.name === name;
            });

            if (p) {
                return p.value;
            }

            return '';
        },

        flatten(treeObj, idAttr, parentAttr, childrenAttr, levelAttr) {
            if (!idAttr) idAttr = 'id';
            if (!parentAttr) parentAttr = 'parent';
            if (!childrenAttr) childrenAttr = 'children';
            if (!levelAttr) levelAttr = 'level';
            let self = this;

            function flattenChild(childObj, parentId, level, parent) {
                let array = [];

                let childCopy = childObj;
                childCopy[levelAttr] = level;
                childCopy[parentAttr] = parentId;

                childCopy['__parent'] = parent;
                self.$set(childCopy, '__expanded', false);
                self.$set(childCopy, '__visible', false);
                // childCopy['__expanded'] = false;
                array.push(childCopy);

                array = array.concat(processChildren(childObj, level));

                return array;
            }

            function processChildren(obj, level) {
                if (!level) level = 0;
                let array = [];

                obj.children.forEach(function(childObj) {
                    array = array.concat(flattenChild(childObj, obj[idAttr], level+1, obj));
                });

                return array;
            }

            return processChildren(treeObj);
        },
        calculateMargin(level) {
            const margin = 20;
            if (level === 1) {
                return margin;
            }

            return level * margin + 15 + (level > 2 ? 15 : 0);
        },
        calculatePadding(product) {
            return 'margin-left: ' + this.calculateMargin(product.level) + 'px !important;';
        },
        makeRequest() {
            this.showLoading = true;
            this.fetch({
                page: this.currentPage,
                perPage: this.perPage
            }, (totalItems, items) => {
                this.totalItems = totalItems;
                this.items = this.initItems(items);
                this.showLoading = false;
            });
        },
    },

    computed: {
        tableStyles() {
            return 'min-height: ' + (window.innerHeight - 200) + 'px';
        },
        minHeight() {
            return 'min-height: ' + (window.innerHeight - 400) + 'px';
        }
    },
    created() {
        this.makeRequest();
    },
};
</script>
<style>
table.overview-table tbody {
    border: none;
}
.overview-table tbody.column-search td {
    padding: 6px 10px 6px 10px;
    /*background-color: #f3f2f7;*/
}

table td.hierarchy-control {
    /*background-color: #fbfbfb;*/
    /*border-right: 1px solid #e9e9e9;*/
    padding-right: 0;
}
table.overview-table td, table.overview-table thead th {
    white-space: nowrap;
}

.hierarchy-control .arrow {
    margin-left: auto;
    margin-right: 8px;
}
</style>
